import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Table,
  Checkbox,
  Badge,
  Alert,
  ActionIcon,
  Tooltip
} from '@mantine/core';
import { IconCheck, IconX, IconUsers } from '@tabler/icons-react';
import { Participant } from 'pages/app/LIA/AssessmentSchedules/utils/schema';

interface Module {
  moduleId: string;
  moduleName: string;
  courseCode?: string;
  description?: string;
}

/* eslint-disable */
interface ModuleAssignmentModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (updatedParticipants: Participant[]) => void;
  participants: Participant[];
  availableModules: Module[];
  statusKurikulum?: boolean;
  mode: 'single' | 'multiple';
  selectedParticipant?: Participant | null;
}
/* eslint-enable */

const ModuleAssignmentModal: React.FC<ModuleAssignmentModalProps> = ({
  opened,
  onClose,
  onSubmit,
  participants,
  availableModules,
  statusKurikulum = false,
  mode,
  selectedParticipant
}) => {
  /* eslint-disable */
  const [participantAssignments, setParticipantAssignments] = useState<Record<string, string[]>>(
    {}
  );
  const [bulkAction, setBulkAction] = useState<'assign' | 'unassign' | null>(null);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  /* eslint-enable */

  // Initialize assignments when modal opens
  useEffect(() => {
    if (opened) {
      const assignments: Record<string, string[]> = {};

      if (mode === 'single' && selectedParticipant) {
        assignments[selectedParticipant.binusianId] = [...(selectedParticipant.listModule || [])];
      } else {
        participants.forEach((participant) => {
          assignments[participant.binusianId] = [...(participant.listModule || [])];
        });
      }

      setParticipantAssignments(assignments);
      setSelectedModules([]);
      setBulkAction(null);
    }
  }, [opened, participants, selectedParticipant, mode]);

  // Toggle module assignment for a participant
  const toggleModuleForParticipant = (participantId: string, moduleId: string) => {
    setParticipantAssignments((prev) => {
      const currentAssignments = prev[participantId] || [];
      const newAssignments = currentAssignments.includes(moduleId)
        ? currentAssignments.filter((id) => id !== moduleId)
        : [...currentAssignments, moduleId];

      return {
        ...prev,
        [participantId]: newAssignments
      };
    });
  };

  // Toggle module selection for bulk operations
  const toggleModuleSelection = (moduleId: string) => {
    setSelectedModules((prev) =>
      prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId]
    );
  };

  // Submit changes
  const handleSubmit = () => {
    const updatedParticipants = participants.map((participant) => ({
      ...participant,
      listModule: participantAssignments[participant.binusianId] || []
    }));

    onSubmit(updatedParticipants);
    onClose();
  };

  // Get target participants for display
  const targetParticipants =
    mode === 'single' && selectedParticipant ? [selectedParticipant] : participants;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        mode === 'single'
          ? `Assign ${statusKurikulum ? 'Courses' : 'Modules'} - ${selectedParticipant?.name}`
          : `Bulk Assign ${statusKurikulum ? 'Courses' : 'Modules'} (${participants.length} participants)`
      }
      size='xl'
      padding='lg'
    >
      <Stack gap='md'>
        {/* Module/Course List */}
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 50 }}>
                  <Checkbox
                    checked={selectedModules.length === availableModules.length}
                    indeterminate={
                      selectedModules.length > 0 && selectedModules.length < availableModules.length
                    }
                    onChange={(event) => {
                      if (event.currentTarget.checked) {
                        setSelectedModules(availableModules.map((m) => m.moduleId));
                      } else {
                        setSelectedModules([]);
                      }
                    }}
                  />
                </Table.Th>
                <Table.Th>{statusKurikulum ? 'Course' : 'Module'}</Table.Th>
                {targetParticipants.map((participant) => (
                  <Table.Th key={participant.binusianId} style={{ width: 120 }}>
                    <div>
                      <Text size='xs' fw={500} lineClamp={1}>
                        {participant.name}
                      </Text>
                      <Text size='xs' c='dimmed'>
                        {participant.binusianId}
                      </Text>
                    </div>
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {availableModules.map((module) => (
                <Table.Tr key={module.moduleId}>
                  <Table.Td>
                    <Checkbox
                      checked={selectedModules.includes(module.moduleId)}
                      onChange={() => toggleModuleSelection(module.moduleId)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <div>
                      <Text size='sm' fw={500}>
                        {module.moduleName}
                      </Text>
                      {module.courseCode && (
                        <Text size='xs' c='dimmed'>
                          {module.courseCode}
                        </Text>
                      )}
                    </div>
                  </Table.Td>
                  {targetParticipants.map((participant) => {
                    const isAssigned = participantAssignments[participant.binusianId]?.includes(
                      module.moduleId
                    );
                    return (
                      <Table.Td key={participant.binusianId}>
                        <Tooltip label={isAssigned ? 'Click to unassign' : 'Click to assign'}>
                          <ActionIcon
                            variant={isAssigned ? 'filled' : 'outline'}
                            color={isAssigned ? 'green' : 'gray'}
                            onClick={() =>
                              toggleModuleForParticipant(participant.binusianId, module.moduleId)
                            }
                          >
                            {isAssigned ? <IconCheck size={16} /> : <IconX size={16} />}
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>

        {/* Summary */}
        <Alert icon={<IconUsers size={16} />} color='gray' variant='light'>
          <Text size='sm'>
            Assignment Summary:{' '}
            {targetParticipants.map((participant) => {
              const assignedCount = participantAssignments[participant.binusianId]?.length || 0;
              return (
                <Badge
                  key={participant.binusianId}
                  size='sm'
                  color='blue'
                  style={{ marginRight: 4 }}
                >
                  {participant.name}: {assignedCount}
                </Badge>
              );
            })}
          </Text>
        </Alert>

        {/* Actions */}
        <Group justify='flex-end'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} leftSection={<IconCheck size={16} />}>
            Save Assignments
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModuleAssignmentModal;
